import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { bimIsUniqueActKC2OtherInternalNumber } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderOthersPrice/bimWorkOrderOthersQueries';
import { replacerCost } from 'utils/replacerCost';

import { useDebounce } from '@consta/uikit/useDebounce';
import { useEffect, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormClearErrors, UseFormSetError } from 'react-hook-form/dist/types/form';
import { useDispatch } from 'react-redux';

interface IUseBimIsUniqueActKC2OtherInternalNumber {
    workOrderId?: number;
    actKC2OtherId?: number;
    setError: UseFormSetError<any>;
    clearErrors: UseFormClearErrors<any>;
    control: Control;
}

const requestId = 'bimIsUniqueActKC2OtherInternalNumber';
const numberKey = 'number';

export const useBimIsUniqueActKC2OtherInternalNumber = (props: IUseBimIsUniqueActKC2OtherInternalNumber) => {
    const { workOrderId, actKC2OtherId = null, setError, clearErrors, control } = props;
    const dispatch = useDispatch();

    const [bimIsUniqueActKC2OtherInternalNumberReset] = useResetStoreData({ requestId });

    const actKC2OtherNumber = useWatch({ control, name: numberKey });

    const variables = useMemo(
        () => ({ workOrderId, actKC2OtherId, actKC2OtherNumber: replacerCost(actKC2OtherNumber) }),
        [actKC2OtherId, actKC2OtherNumber, workOrderId],
    );

    const [
        bimIsUniqueActKC2OtherInternalNumberData,
        bimIsUniqueActKC2OtherInternalNumberStatus,
        ,
        bimIsUniqueActKC2OtherInternalNumberMakeRequest,
    ] = useRequest({
        requestId,
        query: bimIsUniqueActKC2OtherInternalNumber,
        withEffect: false,
    });

    const request = useDebounce(() => {
        if (actKC2OtherNumber) {
            dispatch(bimIsUniqueActKC2OtherInternalNumberMakeRequest(variables));
        }
    }, 300);

    useEffect(() => {
        bimIsUniqueActKC2OtherInternalNumberReset(undefined);
    }, [bimIsUniqueActKC2OtherInternalNumberReset]);

    useEffect(() => {
        if (actKC2OtherNumber) {
            request();
        } else {
            clearErrors(numberKey);
        }
    }, [actKC2OtherNumber, clearErrors, request]);

    useEffect(() => {
        if (
            actKC2OtherNumber &&
            bimIsUniqueActKC2OtherInternalNumberStatus === RequestStatus.SUCCESS &&
            !bimIsUniqueActKC2OtherInternalNumberData?.bimIsUniqueActKC2OtherInternalNumber?.result
        ) {
            setError(numberKey, { message: 'Акт с таким номером уже существует' });
        } else {
            clearErrors(numberKey);
        }
    }, [
        actKC2OtherNumber,
        bimIsUniqueActKC2OtherInternalNumberData?.bimIsUniqueActKC2OtherInternalNumber?.result,
        bimIsUniqueActKC2OtherInternalNumberStatus,
        clearErrors,
        setError,
    ]);
};
